import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import TableHelperPanel from './TableHelperPanel';
import TableWithPagination from './TableWithPagination';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const EditVisualisasi = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [form, setForm] = useState({
    judul: '',
    deskripsi: '',
    jenis_grafik: 'bar',
    query_sql: '',
    database: '',
    parameter_x: '',
    parameter_y: '',
    group_by: ''
  });

  const [queryResult, setQueryResult] = useState([]);
  const [queryColumns, setQueryColumns] = useState([]);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExecutingQuery, setIsExecutingQuery] = useState(false);
  const [isRenderingVisualization, setIsRenderingVisualization] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [showVisualization, setShowVisualization] = useState(false);
  const [showTableWithChart, setShowTableWithChart] = useState(false);
  const [hiddenTables, setHiddenTables] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hiddenTables') || '[]'); }
    catch { return []; }
  });
  const [queryParams, setQueryParams] = useState([{ name: '', value: '' }]);

  const addQueryParam = () => setQueryParams(prev => [...prev, { name: '', value: '' }]);
  const removeQueryParam = (i) => setQueryParams(prev => prev.filter((_, idx) => idx !== i));
  const handleParamChange = (i, field, value) => {
    setQueryParams(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));
  };

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setStatus('');
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);

      try {
        const [dbResponse, visResponse] = await Promise.all([
          fetch('/api/databases'),
          fetch(`/api/visualizations/${id}`)
        ]);

        const databases = await dbResponse.json();
        const visualisasi = await visResponse.json();

        if (!dbResponse.ok) {
          throw new Error('Gagal mengambil daftar database');
        }

        if (!visResponse.ok) {
          throw new Error(visualisasi.error || 'Gagal mengambil data visualisasi');
        }

        setAvailableDatabases(databases || []);

        const databaseId = visualisasi.penyimpanan_database || visualisasi.database || '';

        setForm({
          judul: visualisasi.judul || '',
          deskripsi: visualisasi.deskripsi || '',
          jenis_grafik: visualisasi.jenis_grafik || 'bar',
          query_sql: visualisasi.query_sql || '',
          database: databaseId,
          parameter_x: visualisasi.parameter_x || '',
          parameter_y: visualisasi.parameter_y || '',
          group_by: visualisasi.group_by || ''
        });

        if (visualisasi.parameter_query) {
          try {
            const savedParams = typeof visualisasi.parameter_query === 'string'
              ? JSON.parse(visualisasi.parameter_query)
              : visualisasi.parameter_query;
            const entries = Object.entries(savedParams);
            if (entries.length > 0) {
              setQueryParams(entries.map(([name, value]) => ({ name, value: String(value) })));
            }
          } catch (_) {}
        }

        if (visualisasi.tampilkan_tabel) {
          setShowTableWithChart(!!visualisasi.tampilkan_tabel);
        }

        if (visualisasi.chart_data) {
          try {
            const parsedChartData = typeof visualisasi.chart_data === 'string'
              ? JSON.parse(visualisasi.chart_data)
              : visualisasi.chart_data;

            setQueryResult(parsedChartData || []);

            if (parsedChartData && parsedChartData.length > 0) {
              setQueryColumns(Object.keys(parsedChartData[0]));
              setChartData(parsedChartData);
              setShowVisualization(true);
            }
          } catch (error) {
            console.error('Error parsing chart_data:', error);
          }
        }
      } catch (error) {
        console.error('Error loading edit visualization:', error);
        setStatus(`✗ ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [id]);

  const handleRunQuery = async () => {
    if (!form.database || !form.query_sql) {
      setStatus('✗ Pilih database dan isi query SQL terlebih dahulu.');
      return;
    }

    if (hiddenTables.length > 0) {
      const queryLower = form.query_sql.toLowerCase();
      const blocked = hiddenTables.filter(t => new RegExp(`\\b${t.toLowerCase()}\\b`).test(queryLower));
      if (blocked.length > 0) {
        setStatus(`✗ Query menggunakan tabel yang disembunyikan: ${blocked.join(', ')}`);
        return;
      }
    }

    setIsExecutingQuery(true);
    setStatus('Menjalankan query...');

    try {
      const response = await fetch('/api/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: form.query_sql,
          databaseId: form.database,
          params: Object.fromEntries(
            queryParams.filter(p => p.name && p.value).map(p => [p.name, p.value])
          )
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Query gagal dijalankan');
      }

      const rows = Array.isArray(result) ? result : [];
      setQueryResult(rows);
      setQueryColumns(rows.length > 0 ? Object.keys(rows[0]) : []);
      setChartData([]);
      setShowVisualization(false);
      setShowTableWithChart(false);

      setStatus(`✓ Query berhasil dijalankan. Jumlah baris: ${rows.length}`);

      if (rows.length > 0) {
        const columns = Object.keys(rows[0]);

        setForm(prev => ({
          ...prev,
          parameter_x: prev.parameter_x || columns[0] || '',
          parameter_y: prev.parameter_y || columns[1] || columns[0] || ''
        }));
      }
    } catch (error) {
      console.error('Error running query:', error);
      setStatus(`✗ ${error.message}`);
    } finally {
      setIsExecutingQuery(false);
    }
  };

  const handleRunVisualization = () => {
    if (isRenderingVisualization) return;
    if (!form.parameter_x || !form.parameter_y || queryResult.length === 0) return;

    setIsRenderingVisualization(true);
    setTimeout(() => {
      try {
        const data = queryResult.map(item => ({
          ...item,
          [form.parameter_y]: parseFloat(item[form.parameter_y]) || 0
        }));

        setChartData(data);
        setShowVisualization(true);
      } finally {
        setIsRenderingVisualization(false);
      }
    }, 0);
  };

  const renderChart = () => {
    if (!showVisualization || chartData.length === 0) return null;
    const { parameter_x: xAxis, parameter_y: yAxis } = form;
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

    switch (form.jenis_grafik) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxis} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey={yAxis} fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxis} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey={yAxis} stroke="#8884d8" activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart>
              <CartesianGrid />
              <XAxis type="number" dataKey={xAxis} name={xAxis} />
              <YAxis type="number" dataKey={yAxis} name={yAxis} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name={`${xAxis} vs ${yAxis}`} data={chartData} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={150}
                fill="#8884d8"
                dataKey={yAxis}
                nameKey={xAxis}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'table':
        return <TableWithPagination data={chartData} />;
      default:
        return null;
    }
  };

  const handleSave = async () => {
    const isTableType = form.jenis_grafik === 'table';
    if (
      !form.judul ||
      !form.jenis_grafik ||
      !form.query_sql ||
      !form.database ||
      (!isTableType && (!form.parameter_x || !form.parameter_y))
    ) {
      setStatus('✗ Judul, jenis grafik, database, query, sumbu X, dan sumbu Y wajib diisi.');
      return;
    }

    const firstCol = queryColumns[0] || form.parameter_x || '';
    const lastCol = queryColumns[queryColumns.length - 1] || form.parameter_y || firstCol;

    setIsSaving(true);
    setStatus('Menyimpan perubahan visualisasi...');

    try {
      const response = await fetch(`/api/visualizations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          judul: form.judul,
          deskripsi: form.deskripsi,
          jenis_grafik: form.jenis_grafik,
          parameter_x: isTableType ? firstCol : form.parameter_x,
          parameter_y: isTableType ? lastCol : form.parameter_y,
          group_by: isTableType ? null : (form.group_by || null),
          query_sql: form.query_sql,
          parameter_query: (() => {
            const obj = Object.fromEntries(
              queryParams.filter(p => p.name && p.value).map(p => [p.name, p.value])
            );
            return Object.keys(obj).length > 0 ? JSON.stringify(obj) : null;
          })(),
          chart_data: '[]',
          penyimpanan_database: Number(form.database),
          tampilkan_tabel: showTableWithChart ? 1 : 0
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || 'Gagal menyimpan visualisasi');
      }

      setStatus('✓ Visualisasi berhasil diperbarui.');
      setTimeout(() => {
        navigate('/visualisasi');
      }, 800);
    } catch (error) {
      console.error('Error saving visualization:', error);
      setStatus(`✗ ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    boxSizing: 'border-box'
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeTabProp="visualisasi">
        {/* FIX: dashboard-content diberi minWidth 0 agar tidak overflow dari flex parent */}
        <div className="dashboard-content" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div className="data-explorer">
            <h1>Edit Visualisasi</h1>

            {isLoading ? (
              <div style={{ padding: '20px' }}>Memuat data visualisasi...</div>
            ) : (
              <>
                <div className="upload-section">
                  <h2>Form Edit Visualisasi</h2>

                  <div className="upload-container" style={{ display: 'grid', gap: '15px', minWidth: 0, gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Database
                      </label>
                      <select
                        value={form.database}
                        onChange={(e) => handleChange('database', e.target.value)}
                        style={inputStyle}
                      >
                        <option value="">Pilih database</option>
                        {availableDatabases.map(db => (
                          <option key={db.id_database} value={db.id_database}>
                            {db.name} ({db.host}:{db.port})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* FIX: wrapper TableHelperPanel dengan minWidth 0 agar tidak melar */}
                    <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
                      <TableHelperPanel databaseId={form.database} hiddenTables={hiddenTables} />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Judul Visualisasi
                      </label>
                      <input
                        type="text"
                        value={form.judul}
                        onChange={(e) => handleChange('judul', e.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Deskripsi
                      </label>
                      <textarea
                        value={form.deskripsi}
                        onChange={(e) => handleChange('deskripsi', e.target.value)}
                        rows={3}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Query SQL
                      </label>
                      <textarea
                        value={form.query_sql}
                        onChange={(e) => handleChange('query_sql', e.target.value)}
                        rows={7}
                        placeholder="Contoh: SELECT DATE(waktu) AS tanggal, COUNT(*) AS jumlah FROM LogAktivitas GROUP BY DATE(waktu)"
                        style={{ ...inputStyle, fontFamily: 'monospace' }}
                      />
                    </div>

                    <div style={{ marginTop: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                        Parameter Query
                      </label>
                      {queryParams.map((param, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Nama Parameter"
                            value={param.name}
                            onChange={(e) => handleParamChange(i, 'name', e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: 0 }}
                          />
                          <input
                            type="text"
                            placeholder="Nilai Parameter"
                            value={param.value}
                            onChange={(e) => handleParamChange(i, 'value', e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minWidth: 0 }}
                          />
                          <button
                            type="button"
                            onClick={() => removeQueryParam(i)}
                            style={{ padding: '8px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}
                          >
                            Hapus
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addQueryParam}
                        style={{ padding: '8px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Tambah Parameter
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleRunQuery}
                      disabled={isExecutingQuery}
                      style={{
                        padding: '10px',
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isExecutingQuery ? 'not-allowed' : 'pointer',
                        opacity: isExecutingQuery ? 0.6 : 1
                      }}
                    >
                      {isExecutingQuery ? 'Menjalankan Query...' : 'Jalankan Query'}
                    </button>

                    {queryResult.length > 0 && form.jenis_grafik === 'table' && (
                      <div style={{ minWidth: 0 }}>
                        <h3>Tabel Visualisasi</h3>
                        <TableWithPagination data={queryResult} />
                      </div>
                    )}

                    {queryResult.length > 0 && form.jenis_grafik !== 'table' && (
                      <div style={{ minWidth: 0 }}>
                        <h3>Preview Hasil Query</h3>
                        <div
                          className="table-scroll"
                          style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}
                        >
                          <table className="data-table" style={{ whiteSpace: 'nowrap' }}>
                            <thead>
                              <tr>
                                <th>#</th>
                                {queryColumns.map(col => (
                                  <th key={col}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {queryResult.slice(0, 10).map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  <td>{rowIndex + 1}</td>
                                  {queryColumns.map(col => (
                                    <td key={col}>{String(row[col] ?? '')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {queryColumns.length > 0 && (
                      <>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                            Jenis Grafik
                          </label>
                          <select
                            value={form.jenis_grafik}
                            onChange={(e) => handleChange('jenis_grafik', e.target.value)}
                            style={inputStyle}
                          >
                            <option value="bar">Bar Chart</option>
                            <option value="line">Line Chart</option>
                            <option value="pie">Pie Chart</option>
                            <option value="scatter">Scatter Chart</option>
                            <option value="table">Tabel</option>
                          </select>
                        </div>

                        {form.jenis_grafik !== 'table' && (
                          <>
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Sumbu X atau Kategori
                              </label>
                              <select
                                value={form.parameter_x}
                                onChange={(e) => handleChange('parameter_x', e.target.value)}
                                style={inputStyle}
                              >
                                <option value="">Pilih kolom</option>
                                {queryColumns.map(col => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Sumbu Y atau Nilai
                              </label>
                              <select
                                value={form.parameter_y}
                                onChange={(e) => handleChange('parameter_y', e.target.value)}
                                style={inputStyle}
                              >
                                <option value="">Pilih kolom</option>
                                {queryColumns.map(col => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                Group By Opsional
                              </label>
                              <select
                                value={form.group_by || ''}
                                onChange={(e) => handleChange('group_by', e.target.value)}
                                style={inputStyle}
                              >
                                <option value="">Tidak ada</option>
                                {queryColumns.map(col => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            </div>

                            <button
                              type="button"
                              onClick={handleRunVisualization}
                              disabled={isRenderingVisualization}
                              style={{
                                padding: '10px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isRenderingVisualization ? 'not-allowed' : 'pointer',
                                fontWeight: '600',
                                opacity: isRenderingVisualization ? 0.7 : 1
                              }}
                            >
                              {isRenderingVisualization ? 'Sedang memproses visualisasi...' : 'Jalankan Visualisasi'}
                            </button>

                            {isRenderingVisualization ? (
                              <div style={{ marginTop: '10px', padding: '20px', textAlign: 'center' }}>
                                Sedang memproses visualisasi...
                              </div>
                            ) : showVisualization && (
                              <div style={{ marginTop: '10px', minWidth: 0, overflow: 'hidden' }}>
                                <h3>Preview Visualisasi</h3>
                                {renderChart()}
                                <div style={{ marginTop: '12px' }}>
                                  <button
                                    type="button"
                                    onClick={() => setShowTableWithChart(prev => !prev)}
                                    style={{
                                      padding: '6px 14px',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd',
                                      cursor: 'pointer',
                                      backgroundColor: showTableWithChart ? '#1976d2' : '#f5f5f5',
                                      color: showTableWithChart ? 'white' : '#333',
                                      fontWeight: '500',
                                      marginBottom: '8px'
                                    }}
                                  >
                                    {showTableWithChart ? 'Sembunyikan Tabel Data' : 'Tampilkan Tabel Data'}
                                  </button>
                                  {showTableWithChart && <TableWithPagination data={queryResult} />}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{
                          flex: 1,
                          padding: '10px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          opacity: isSaving ? 0.6 : 1,
                          fontWeight: '600'
                        }}
                      >
                        {isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate('/visualisasi')}
                        style={{
                          padding: '10px 16px',
                          backgroundColor: '#9e9e9e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Batal
                      </button>
                    </div>

                    {status && (
                      <div className={status.includes('✓') ? 'save-success-message' : 'input-error-message'}>
                        {status}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default EditVisualisasi;
